import { InvalidConfirmEmailCodeError } from "@/api/IdentityApi";
import { TooManyRequestsError } from "@/api/Errors";
import { AuthLayout } from "@/components/layouts/AuthLayout";
import { useIdentity } from "@/components/contexts/IdentityContext/UseIdentity";
import { LoadingDots } from "@/components/ui/LoadingDots";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/shadcn/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/shadcn/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/shadcn/input-otp";
import { Heading, Text } from "@/components/ui/Typography";
import { zodResolver } from "@hookform/resolvers/zod";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { Mail } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useSearchParams } from "react-router";
import { z } from "zod";
import { toast } from "sonner";
import { animDelay } from "@/lib/utils";

const RESEND_COOLDOWN_SECONDS = 30;

const VerifyEmailSchema = z.object({
  code: z
    .string()
    .nonempty("Code is required")
    .length(6, "Code must be 6 digits long"),
});

type VerifyEmailData = z.infer<typeof VerifyEmailSchema>;

function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const { verifyEmailCode, resendEmailConfirmation } = useIdentity();
  const { state } = useLocation();

  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [isResending, setIsResending] = useState(false);
  const [isTooManyRequests, setIsTooManyRequests] = useState(false);

  const form = useForm<VerifyEmailData>({
    resolver: zodResolver(VerifyEmailSchema),
    defaultValues: {
      code: "",
    },
  });

  const onSubmit = useCallback(
    async ({ code }: VerifyEmailData) => {
      try {
        await verifyEmailCode({ email, code });
        window.location.href = state?.from || "/";
      } catch (error) {
        if (error instanceof InvalidConfirmEmailCodeError) {
          form.setError("root", {
            message: "Invalid code, please try again.",
          });
        } else {
          form.setError("root", {
            message:
              "An error occurred while verifying the email code. Please try again.",
          });
        }
      }
    },
    [email, verifyEmailCode, form, state],
  );

  const doSubmit = useCallback(() => {
    form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendCode = useCallback(async () => {
    if (!email || resendCooldown > 0 || isResending || isTooManyRequests)
      return;

    setIsResending(true);

    try {
      await resendEmailConfirmation({ email });

      toast.success("We've sent you a new code.");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      if (error instanceof TooManyRequestsError) {
        setIsTooManyRequests(true);
        toast.error(
          "You've requested too many codes. Please try again in 15 minutes.",
        );
      } else {
        toast.error("Failed to resend code. Please try again.");
      }
    } finally {
      setIsResending(false);
    }
  }, [
    email,
    resendCooldown,
    isResending,
    isTooManyRequests,
    resendEmailConfirmation,
  ]);

  if (!email) {
    return <Navigate to={`/signin`} state={state} />;
  }

  return (
    <AuthLayout>
      <div className="flex flex-col items-center text-center">
        <header
          className="anim-slide-up flex flex-col items-center gap-4"
          style={animDelay(50)}
        >
          <Logo variant="A" className="mb-4 w-[144px] sm:w-[156px]" />

          <div
            className="anim-scale-in flex size-16 items-center justify-center rounded-2xl bg-primary/10"
            style={animDelay(150)}
          >
            <Mail size={28} className="text-primary" />
          </div>

          <Heading
            variant="h1"
            weight="medium"
            className="text-3xl sm:text-3xl"
          >
            Check your email
          </Heading>
          <Text variant="text3" className="max-w-xs text-muted-foreground">
            We sent a 6-digit code to{" "}
            <span className="font-semibold text-foreground">{email}</span>
          </Text>
        </header>

        <div className="anim-slide-up mt-8 w-full" style={animDelay(200)}>
          <Form {...form}>
            <form
              className="flex flex-col items-center"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <FormField
                control={form.control}
                name="code"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <InputOTP
                        maxLength={6}
                        pattern={REGEXP_ONLY_DIGITS}
                        {...field}
                        aria-invalid={!!fieldState.error}
                        disabled={form.formState.isSubmitting}
                        onComplete={doSubmit}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                        </InputOTPGroup>
                        <InputOTPGroup>
                          <InputOTPSlot index={1} />
                        </InputOTPGroup>
                        <InputOTPGroup>
                          <InputOTPSlot index={2} />
                        </InputOTPGroup>
                        <InputOTPGroup>
                          <InputOTPSlot index={3} />
                        </InputOTPGroup>
                        <InputOTPGroup>
                          <InputOTPSlot index={4} />
                        </InputOTPGroup>
                        <InputOTPGroup>
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="anim-slide-up mt-8 w-full" style={animDelay(300)}>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? (
                    <LoadingDots color="current" />
                  ) : (
                    "Verify email"
                  )}
                </Button>
              </div>

              <div
                className="anim-fade-in mt-4 flex flex-col items-center gap-2"
                style={animDelay(400)}
              >
                {isTooManyRequests ? (
                  <Text
                    variant="caption"
                    className="text-center text-destructive"
                  >
                    You've requested too many codes. Please try again later.
                  </Text>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleResendCode}
                    disabled={resendCooldown > 0 || isResending}
                    className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                  >
                    {isResending ? (
                      <>
                        <LoadingDots color="current" />
                        Sending...
                      </>
                    ) : resendCooldown > 0 ? (
                      `Resend code (${resendCooldown}s)`
                    ) : (
                      "Didn't get it? Resend code"
                    )}
                  </Button>
                )}
              </div>

              {form.formState.errors.root && (
                <div className="anim-slide-down">
                  <Text variant="text3" className="mt-4 text-destructive">
                    {form.formState.errors.root.message}
                  </Text>
                </div>
              )}
            </form>
          </Form>
        </div>
      </div>
    </AuthLayout>
  );
}

export default VerifyEmailPage;
